import React, { useState, useEffect, useMemo, useRef } from "react";
import * as dateFns from "date-fns";
import { useTranslation } from "react-i18next";

import Modal from "../../common/Modal";
import Button from "../../common/FormFields/Button";
import Search from "../../common/FormFields/Search";
import CustomSelect from "../../common/FormFields/Select";
import OverWriteModal from "./OverWriteModal";
import ForceConfirmModal from "./ForceConfirmModal";
import {
  prepareTimeslotForUpdating,
  prepareTimeslotForCreation,
} from "./helpers";
import {
  getStartOfAllDay,
  getStartOfWorkDay,
  getTimeIntervals,
  timestampToIso,
  timestampToWorkingTime,
  workingTimeToTimestamp,
  getAppointmentTypeDisplayName,
} from "../../../services/helpers";
import { ALL_BLOOD_TYPES, BLOOD_TITER } from "./constants";
import { get, preCheckBooking } from '../../../services/fetch';

const BookAppointment = (props) => {
  const [earliestDateWarning, setEarliestDateWarning] = useState(null);
  const [quarantineWarning, setQuarantineWarning] = useState(null);
  const {
    selectedTimeslot,
    bloodBankId,
    bloodBankName,
    bookAppointmentModalIsOpen,
    toggleBookAppointmentModal,
    userByPnr,
    isOverwriting,
    overwritingError,
    getUserByPnr,
    bookAppointment,
    cancelAppointmentBooking,
    overwriteTimeslot,
    isOverwriteModalOpened,
    toggleOverwriteModal,
    appointmentTypes,
    recurrenceRule,
    startOverwriteTimeslot,
    finishOverwriteTimeslot,
    currentAppointment,
    isDayPage,
    updateDonor,
    isSlotClickFromCell,
    selectedTime,
    selectedResourceId,
    currentDate,
    setTimeslotRequest,
    setIsDirectlyBooking,
    calendar,
  } = props;
  const { t } = useTranslation();
  const [userPnr, setUserPnr] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [currentAppointments, setCurrentAppointments] = useState([]);
  const [chosenAppointment, setChosenAppointment] = useState(null);
  const [isTiterMatch, setIsTiterMatch] = useState(false);
  const [isDonorTypeMatch, setIsDonorTypeMatch] = useState(false);
  const [isInQuarantine, setIsInQuarantine] = useState(false);
  const [isAppointmentReschedule, setIsAppointmentReschedule] = useState(false);
  const [isDonorStatusPending, setIsDonorStatusPending] = useState(false);
  const [isConsultationAppointment, setIsConsultationAppointment] = useState(false);
  const [availableTimeSlots] = useState(getTimeIntervals());
  const [timeFrom, setTimeFrom] = useState(availableTimeSlots[0]);
  const [timeTo, setTimeTo] = useState(availableTimeSlots[0]);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState(null);
  const [overlapModalOpen, setOverlapModalOpen] = useState(false);
  const [forceConfirmModalOpen, setForceConfirmModalOpen] = useState(false);
  const [forceConfirmData, setForceConfirmData] = useState(null);
  const [eligibleAppointmentTypes, setEligibleAppointmentTypes] = useState([]);
  const [bookingEligibilityMissing, setBookingEligibilityMissing] = useState(false);
  const [eligibilityCheckLoading, setEligibilityCheckLoading] = useState(false);
  const finishLoadingCalledRef = useRef(false);

  useEffect(() => {
    if (forceConfirmModalOpen && isOverwriting && !finishLoadingCalledRef.current) {
      finishLoadingCalledRef.current = true;
      finishOverwriteTimeslot();
    }
  }, [forceConfirmModalOpen, isOverwriting, finishOverwriteTimeslot]);

  useEffect(() => {
    if (!forceConfirmModalOpen) {
      finishLoadingCalledRef.current = false;
    }
  }, [forceConfirmModalOpen]);

  const isNewRegistrationOrPendingRegistration =
    selectedAppointmentType?.donorStatus === "new" ||
    selectedAppointmentType?.donorStatus === "pending";

  const resourceTimeslots = useMemo(() => {
    if (!calendar) return [];
    const resourceId = selectedTimeslot?.resourceId || selectedResourceId;
    if (!resourceId) return [];
    const resource = calendar.find((res) => res.resourceId === resourceId);
    return resource?.timeslots || [];
  }, [calendar, selectedResourceId, selectedTimeslot]);

  const getOverlapInfo = ({
    bookingStart,
    bookingEnd,
    currentTimeslotId,
    includeSelf,
  }) => {
    const overlaps = [];
    resourceTimeslots.forEach((slot) => {
      (slot.appointments || []).forEach((app) => {
        const isSameAppointment =
          app.startDate === bookingStart && slot.timeslotId === currentTimeslotId;
        if (!includeSelf && isSameAppointment) return;
        const appStart = app.startDate;
        const appEnd = app.endDate;
        const isOverlap = appStart < bookingEnd && appEnd > bookingStart;
        if (isOverlap) {
          overlaps.push({
            timeslot: slot,
            appointment: app,
            status: app.status,
            hasReservation: !!app.reservation,
          });
        }
      });
    });
    const isReserved = (item) =>
      item.hasReservation || (item.status && item.status !== "pending");

    const reservedOverlaps = overlaps.filter(isReserved);
    const availableOverlaps = overlaps.filter((item) => !isReserved(item));

    return { reservedOverlaps, availableOverlaps };
  };

  const addExceptionToTimeslot = async (slot, overlapAppointments) => {
    const base = {...prepareTimeslotForUpdating(slot), rule: slot.initialSettings};
    const existingExceptions = base.exceptionHoursOnDates || [];
    const updatedExceptions = [...existingExceptions];
    

    overlapAppointments.forEach(({ appointment }) => {
      const exception = {
        from: timestampToIso(appointment.startDate),
        to: timestampToIso(appointment.endDate),
        date: timestampToIso(getStartOfWorkDay(appointment.startDate)),
      };
      updatedExceptions.push(exception);
    });

    const payload = {
      ...base,
      exceptionHoursOnDates: updatedExceptions,
    };

    await setTimeslotRequest(payload, slot.appointmentType?.duration);
  };

  const handleOverlaps = async ({
    bookingStart,
    bookingEnd,
    currentTimeslotId,
    applyAvailableExceptions,
  }) => {
    if(!isSlotClickFromCell && isConsultationAppointment) {
      return { blocked: false, hadAvailableOverlaps: false };
    }

    const { reservedOverlaps, availableOverlaps } = getOverlapInfo({
      bookingStart,
      bookingEnd,
      currentTimeslotId,
      includeSelf: !!applyAvailableExceptions,
    });

    if (reservedOverlaps.length > 0) {
      setOverlapModalOpen(true);
      return { blocked: true, hadAvailableOverlaps: false };
    }

    if (availableOverlaps.length > 0) {
      const grouped = availableOverlaps.reduce((acc, item) => {
        const key = item.timeslot.timeslotId;
        acc[key] = acc[key] ? [...acc[key], item] : [item];
        return acc;
      }, {});

      for (const key of Object.keys(grouped)) {
        await addExceptionToTimeslot(grouped[key][0].timeslot, grouped[key]);
      }
    }

    return { blocked: false, hadAvailableOverlaps: availableOverlaps.length > 0 };
  };

  useEffect(() => {
    if(selectedResourceId && selectedAppointmentType) {
      const newTimeFrom = availableTimeSlots.find(item => item.value === selectedTime) || availableTimeSlots[0];
      setTimeFrom(newTimeFrom);
      const newTimeToInMilliseconds = workingTimeToTimestamp(newTimeFrom, currentDate) + selectedAppointmentType.duration;
      setTimeTo(timestampToWorkingTime(newTimeToInMilliseconds));
    }
  }, [availableTimeSlots, currentDate, selectedAppointmentType, selectedResourceId, selectedTime])

  useEffect(() => {
    if (userPnr) {
      getUserByPnr(userPnr);
    }
  }, [getUserByPnr, userPnr]);

  useEffect(() => {
    setCurrentUser(userByPnr ? userByPnr : null);
  }, [userByPnr]);

  useEffect(() => {
    if (currentUser && !isSlotClickFromCell) {
      setIsTiterMatch(
        selectedTimeslot.bloodTiter &&
          currentUser?.status !== "new" &&
          !selectedTimeslot.bloodTiter.find(
            (item) => item === currentUser?.bloodTiter,
          ),
      );
      setIsDonorTypeMatch(
        currentUser?.status !== "new" &&
          !selectedTimeslot.donorType.find(
            (item) =>
              item?.rhesus === currentUser?.donorType?.rhesus &&
              item?.bloodType === currentUser?.donorType?.bloodType,
          ),
      );
      setIsInQuarantine(
        currentUser && currentUser.quarantine && currentAppointment
          ? currentUser.quarantine > currentAppointment.startDate
          : false,
      );
      setIsDonorStatusPending(currentUser?.status === "pending");
    }

    if(currentUser && isSlotClickFromCell) {
      setIsConsultationAppointment(currentUser?.status === "pending");
      setIsDonorStatusPending(currentUser?.status === "pending");
      const quarantineDate = new Date(currentUser?.quarantine);
      const quarantineDay = getStartOfAllDay(quarantineDate);

      setIsInQuarantine(
        !isConsultationAppointment && currentUser.quarantine && currentDate
          ? quarantineDay > currentDate
          : false
      );
    }
  }, [currentDate, currentUser, isConsultationAppointment, isSlotClickFromCell, selectedTimeslot, currentAppointment]);

  useEffect(() => {
    let isMounted = true;
    const setDefaults = (mappedTypes, hasMissingEligibility = false) => {
      if (!currentUser) {
        setSelectedAppointmentType(null);
        setBookingEligibilityMissing(false);
        setEligibilityCheckLoading(false);
        return;
      }
      
      if (hasMissingEligibility) {
        setBookingEligibilityMissing(true);
        setSelectedAppointmentType(null);
        setEligibilityCheckLoading(false);
        return;
      }
      
      if (mappedTypes && mappedTypes.length > 0) {
        setBookingEligibilityMissing(false);
        setEligibilityCheckLoading(false);
        return;
      }
      
      const fallback = appointmentTypes.find(
        (item) => item.donorStatus === currentUser.status
      );
      if (fallback) {
        setSelectedAppointmentType(fallback);
        setBookingEligibilityMissing(false);
      }
      setEligibilityCheckLoading(false);
    };

    if (currentUser) {
      setEligibilityCheckLoading(true);

      (async () => {
        try {
          const res = await get(`/timeslots/getEligibleAppointmentTypesForStaff/${currentUser.id}`);
          const items = (res?.content || []).map(entry => ({
            ...entry.appointmentType,
            bookingChecks: entry.bookingChecks || entry.appointmentType?.bookingChecks || null,
            bookableFromDate: entry.bookableFromDate || null,
            donorQuarantine: entry.donorQuarantine || null,
            eligibility: entry.eligibility || null,
          }));
          if (!isMounted) return;
          setEligibleAppointmentTypes(items);
          setDefaults(items, false);
        } catch (err) {
          const isMissingEligibility = err?.body?.error === 'booking_eligibility_missing';
          if (!isMounted) return;
          setEligibleAppointmentTypes([]);
          setDefaults([], isMissingEligibility);
        }
      })();
    } else {
      setEligibleAppointmentTypes([]);
      setDefaults([], false);
    }

    return () => { isMounted = false };
  }, [currentUser, currentDate, appointmentTypes]);

  useEffect(() => {
    setCurrentAppointments(
      selectedTimeslot?.appointments?.filter((item) => item.status === "pending")
        .map((item) => {
          return {
            label: `${dateFns.format(new Date(item.startDate), "HH:mm")} - ${dateFns.format(new Date(item.endDate), "HH:mm")}`,
            value: String(item.startDate),
          };
        }) || [],
    );

    setIsConsultationAppointment(selectedTimeslot?.appointmentType.name === "Consultation" && !isSlotClickFromCell);

  }, [selectedTimeslot, isSlotClickFromCell]);

  useEffect(() => {
    setEarliestDateWarning(null);
    setQuarantineWarning(null);
    if (!selectedAppointmentType || !currentUser) return;

    const resolvedAppointmentDate = isSlotClickFromCell
      ? workingTimeToTimestamp(timeFrom, currentDate)
      : Number(chosenAppointment?.value || 0);

    if (!resolvedAppointmentDate && !isSlotClickFromCell) return;

    const bookableFrom = selectedAppointmentType.bookableFromDate || null;
    if (bookableFrom) {
      try {
        const parts = bookableFrom.split('-').map(p => Number(p));
        if (parts.length === 3) {
          const earliestDate = new Date(parts[0], parts[1] - 1, parts[2]).getTime();
          if (resolvedAppointmentDate && resolvedAppointmentDate < earliestDate) {
            setEarliestDateWarning(selectedAppointmentType.bookableFromDate);
          }
        }
      } catch (e) {
        // ignore parse errors
      }
    }

    const donorQuar = selectedAppointmentType.donorQuarantine || currentUser?.quarantine || null;
    if (selectedAppointmentType.bookingChecks?.donorQuarantine === true && donorQuar) {
      if (resolvedAppointmentDate && resolvedAppointmentDate < donorQuar) {
        setQuarantineWarning(donorQuar);
      }
    }
  }, [selectedAppointmentType, chosenAppointment, currentUser, timeFrom, currentDate, isSlotClickFromCell]);

  const updateDonorStatus = async () => {
    try {
      if (isConsultationAppointment && !isDonorStatusPending) {
        await updateDonor({
          ...currentUser,
          status: "pending",
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getTimeslotLimits = () => {
    const startDate = workingTimeToTimestamp(timeFrom, currentDate);
    const endDate = workingTimeToTimestamp(timeTo, currentDate);
    return { startDate, endDate };
  };

  const getTimeslotRequestBody = (checkForAvailableResources) => {
    if (!selectedAppointmentType) {
      return null;
    }
    const { startDate, endDate } = getTimeslotLimits();
    const bloodTypes = isNewRegistrationOrPendingRegistration || !currentUser?.donorType
      ? ALL_BLOOD_TYPES
      : [currentUser.donorType];
    const bloodTiter =
      currentUser?.status === "established" &&
      currentUser?.titer === "low" &&
      currentUser?.donorType?.bloodType === "o" &&
      currentUser?.donorType?.rhesus === "plus"
        ? [currentUser?.bloodTiter]
        : BLOOD_TITER;

    return {
      ...(!checkForAvailableResources && {
        resourceId: selectedResourceId || selectedTimeslot?.resourceId,
      }),
      donorType: bloodTypes,
      bloodTiter,
      appointmentTypeId: selectedAppointmentType.appointmentTypeId,
      startDate,
      endDate,
      exceptionDates: [],
      exceptionRules: [],
      exceptionHoursOnDates: [],
    };
  };

  const handleSubmit = async () => {
    if (!selectedAppointmentType) {
      return;
    }
    if (currentUser?.nextAppointment !== undefined || (isConsultationAppointment && !isDonorStatusPending)) {
      setIsAppointmentReschedule(true);
      return;
    }
    toggleBookAppointmentModal();
    startOverwriteTimeslot();

    let timeSlotData = selectedTimeslot;
    let requestBodyData = getTimeslotRequestBody();

    const appointmentValue = isSlotClickFromCell ? requestBodyData?.startDate : chosenAppointment.value;
    const bookingStart = Number(appointmentValue);
    const bookingEnd = bookingStart + selectedAppointmentType.duration;

    const needsCustomTimeslot =
      !isSlotClickFromCell &&
      (selectedAppointmentType.appointmentTypeId !== timeSlotData?.appointmentType?.appointmentTypeId ||
        selectedAppointmentType.duration !== timeSlotData?.appointmentType?.duration);

    const overlapResult = await handleOverlaps({
      bookingStart,
      bookingEnd,
      currentTimeslotId: timeSlotData?.timeslotId,
      applyAvailableExceptions: needsCustomTimeslot,
    });
    if (overlapResult?.blocked) {
      finishOverwriteTimeslot();
      return;
    };

    if (isSlotClickFromCell || needsCustomTimeslot) {
      try {
        const preCheckResult = await preCheckBooking({
          donorUid: currentUser.id,
          appointmentTypeId: selectedAppointmentType.appointmentTypeId,
          appointmentStartDate: bookingStart,
          donorType: requestBodyData?.donorType,
          bloodTiter: requestBodyData?.bloodTiter,
        });

        if (preCheckResult?.content?.canBook === false) {
          const result = preCheckResult.content;
          setForceConfirmData({
            failedChecks: result.failedChecks || [],
            details: result.details || {},
            overrideAllowed: !!result.overrideAllowed,
            userId: currentUser?.id,
            bloodBankId,
            bookingStart,
            onForceBookCustom: async () => {
              startOverwriteTimeslot();
              try {
                const tsData = isSlotClickFromCell
                  ? requestBodyData
                  : getTimeslotRequestBody(false);
                if (isSlotClickFromCell) setIsDirectlyBooking(true);
                const created = await setTimeslotRequest(
                  tsData,
                  selectedAppointmentType.duration,
                  true,
                );
                if (!created?.timeslotId) throw new Error('Timeslot creation failed');
                await bookAppointment(
                  bookingStart,
                  created.timeslotId,
                  currentUser.id,
                  bloodBankId,
                  true,
                );
                await updateDonorStatus();
              } finally {
                finishOverwriteTimeslot();
                setIsDirectlyBooking(false);
              }
            },
          });
          finishOverwriteTimeslot();
          setIsDirectlyBooking(false);
          setForceConfirmModalOpen(true);
          return;
        }
      } catch (preCheckErr) {
        if (preCheckErr?.body?.error === 'failed_checks') {
          setForceConfirmData({
            failedChecks: preCheckErr.body.failedChecks,
            details: preCheckErr.body.details,
            overrideAllowed: !!preCheckErr.body.overrideAllowed,
            userId: currentUser?.id,
            bloodBankId,
            bookingStart,
            onForceBookCustom: async () => {
              startOverwriteTimeslot();
              try {
                const tsData = isSlotClickFromCell
                  ? requestBodyData
                  : getTimeslotRequestBody(false);
                if (isSlotClickFromCell) setIsDirectlyBooking(true);
                const created = await setTimeslotRequest(
                  tsData,
                  selectedAppointmentType.duration,
                  true,
                );
                if (!created?.timeslotId) throw new Error('Timeslot creation failed');
                await bookAppointment(
                  bookingStart,
                  created.timeslotId,
                  currentUser.id,
                  bloodBankId,
                  true,
                );
                await updateDonorStatus();
              } finally {
                finishOverwriteTimeslot();
                setIsDirectlyBooking(false);
              }
            },
          });
          finishOverwriteTimeslot();
          setIsDirectlyBooking(false);
          setForceConfirmModalOpen(true);
          return;
        }
        finishOverwriteTimeslot();
        throw preCheckErr;
      }

      if (isSlotClickFromCell) {
        setIsDirectlyBooking(true);
        timeSlotData = await setTimeslotRequest(
          requestBodyData,
          selectedAppointmentType.duration,
          true,
        );
        if (timeSlotData) {
          timeSlotData.appointments = [
            {
              appointmentType: timeSlotData.appointmentType,
              bloodTiter: timeSlotData.bloodTiter,
              donorType: timeSlotData.donorType,
              startDate: timeSlotData.startDate,
              endDate: timeSlotData.endDate,
              startDateISO: timeSlotData.startDateISO,
              status: "pending",
              timeslotId: timeSlotData.timeslotId,
              timeslotStartDate: timeSlotData.startDate,
              titer: timeSlotData.bloodTiter,
            },
          ];
        }
      }

      if (needsCustomTimeslot) {
        const data = getTimeslotRequestBody(false);
        const createdTimeslot = await setTimeslotRequest(
          data,
          selectedAppointmentType.duration,
          true,
        );
        if (!createdTimeslot?.timeslotId) return;

        await bookAppointment(
          bookingStart,
          createdTimeslot.timeslotId,
          currentUser.id,
          bloodBankId,
        );
        await updateDonorStatus();
        finishOverwriteTimeslot();
        return;
      }
    }

    try {
      await bookAppointment(
        bookingStart,
        timeSlotData?.timeslotId,
        currentUser.id,
        bloodBankId,
      );
    } catch (err) {
      if (err && err.body && err.body.error === 'failed_checks') {
        setForceConfirmData({
          failedChecks: err.body.failedChecks,
          details: err.body.details,
          overrideAllowed: !!err.body.overrideAllowed,
          userId: currentUser?.id,
          timeslotId: timeSlotData?.timeslotId,
          bloodBankId,
          bookingStart,
        });
        finishOverwriteTimeslot();
        setIsDirectlyBooking(false);
        setForceConfirmModalOpen(true);
        return;
      }
      finishOverwriteTimeslot();
      throw err;
    }

    await updateDonorStatus();
    finishOverwriteTimeslot();
  };

  const handleSubmitOverwrite = async () => {
    if (!selectedAppointmentType) {
      return;
    }
    toggleBookAppointmentModal();
    startOverwriteTimeslot();
    let timeSlotData = selectedTimeslot;

    let requestBodyData = getTimeslotRequestBody();

    const appointmentValue = isSlotClickFromCell
      ? requestBodyData?.startDate
      : chosenAppointment.value;

    const bookingStart = Number(appointmentValue);
    const bookingEnd = bookingStart + selectedAppointmentType.duration;
    const needsCustomTimeslot =
      !isSlotClickFromCell &&
      (selectedAppointmentType.appointmentTypeId !== timeSlotData?.appointmentType?.appointmentTypeId ||
        selectedAppointmentType.duration !== timeSlotData?.appointmentType?.duration);

    const overlapResult = await handleOverlaps({
      bookingStart,
      bookingEnd,
      currentTimeslotId: timeSlotData?.timeslotId,
      applyAvailableExceptions: needsCustomTimeslot,
    });
    if (overlapResult?.blocked) {
      finishOverwriteTimeslot();
      return;
    };

    const cancelExistingAppointment = async () => {
      if (currentUser?.nextAppointment !== undefined) {
        await cancelAppointmentBooking(
          bloodBankId,
          currentUser.nextAppointment.reservationId,
          currentUser.id,
          currentUser.nextAppointment.startDate,
          bloodBankName,
        );
      }
    };

    if (isSlotClickFromCell || (needsCustomTimeslot && !isConsultationAppointment)) {
      try {
        const preCheckResult = await preCheckBooking({
          donorUid: currentUser.id,
          appointmentTypeId: selectedAppointmentType.appointmentTypeId,
          appointmentStartDate: bookingStart,
          donorType: requestBodyData?.donorType,
          bloodTiter: requestBodyData?.bloodTiter,
        });

        if (preCheckResult?.content?.canBook === false) {
          const result = preCheckResult.content;
          setForceConfirmData({
            failedChecks: result.failedChecks || [],
            details: result.details || {},
            overrideAllowed: !!result.overrideAllowed,
            userId: currentUser?.id,
            bloodBankId,
            bookingStart,
            onForceBookCustom: async () => {
              startOverwriteTimeslot();
              try {
                await cancelExistingAppointment();
                const tsData = isSlotClickFromCell
                  ? requestBodyData
                  : getTimeslotRequestBody(false);
                if (isSlotClickFromCell) setIsDirectlyBooking(true);
                const created = await setTimeslotRequest(
                  tsData,
                  selectedAppointmentType.duration,
                  true,
                );
                if (!created?.timeslotId) throw new Error('Timeslot creation failed');
                await bookAppointment(
                  bookingStart,
                  created.timeslotId,
                  currentUser.id,
                  bloodBankId,
                  true,
                );
                await updateDonorStatus();
              } finally {
                setIsAppointmentReschedule(false);
                finishOverwriteTimeslot();
                setIsDirectlyBooking(false);
              }
            },
          });
          finishOverwriteTimeslot();
          setIsDirectlyBooking(false);
          setForceConfirmModalOpen(true);
          return;
        }
      } catch (preCheckErr) {
        if (preCheckErr?.body?.error === 'failed_checks') {
          setForceConfirmData({
            failedChecks: preCheckErr.body.failedChecks,
            details: preCheckErr.body.details,
            overrideAllowed: !!preCheckErr.body.overrideAllowed,
            userId: currentUser?.id,
            bloodBankId,
            bookingStart,
            onForceBookCustom: async () => {
              startOverwriteTimeslot();
              try {
                await cancelExistingAppointment();
                const tsData = isSlotClickFromCell
                  ? requestBodyData
                  : getTimeslotRequestBody(false);
                if (isSlotClickFromCell) setIsDirectlyBooking(true);
                const created = await setTimeslotRequest(
                  tsData,
                  selectedAppointmentType.duration,
                  true,
                );
                if (!created?.timeslotId) throw new Error('Timeslot creation failed');
                await bookAppointment(
                  bookingStart,
                  created.timeslotId,
                  currentUser.id,
                  bloodBankId,
                  true,
                );
                await updateDonorStatus();
              } finally {
                setIsAppointmentReschedule(false);
                finishOverwriteTimeslot();
                setIsDirectlyBooking(false);
              }
            },
          });
          finishOverwriteTimeslot();
          setIsDirectlyBooking(false);
          setForceConfirmModalOpen(true);
          return;
        }
        finishOverwriteTimeslot();
        throw preCheckErr;
      }

      await cancelExistingAppointment();

      if (isSlotClickFromCell) {
        setIsDirectlyBooking(true);
        timeSlotData = await setTimeslotRequest(
          requestBodyData,
          selectedAppointmentType.duration,
          true,
        );
        timeSlotData.appointments = [
          {
            appointmentType: timeSlotData.appointmentType,
            bloodTiter: timeSlotData.bloodTiter,
            donorType: timeSlotData.donorType,
            startDate: timeSlotData.startDate,
            endDate: timeSlotData.endDate,
            startDateISO: timeSlotData.startDateISO,
            status: "pending",
            timeslotId: timeSlotData.timeslotId,
            timeslotStartDate: timeSlotData.startDate,
            titer: timeSlotData.bloodTiter,
          },
        ];
      }

      if (needsCustomTimeslot && !isConsultationAppointment) {
        const data = getTimeslotRequestBody(false);
        const createdTimeslot = await setTimeslotRequest(
          data,
          selectedAppointmentType.duration,
          true,
        );
        if (!createdTimeslot?.timeslotId) return;

        await bookAppointment(
          bookingStart,
          createdTimeslot.timeslotId,
          currentUser.id,
          bloodBankId,
        );
        await updateDonorStatus();
        setIsAppointmentReschedule(false);
        finishOverwriteTimeslot();
        return;
      }
    } else {
      await cancelExistingAppointment();
    }

    try {
      await bookAppointment(
        bookingStart,
        timeSlotData.timeslotId,
        currentUser.id,
        bloodBankId,
      );
    } catch (err) {
      if (err && err.body && err.body.error === 'failed_checks') {
        setForceConfirmData({
          failedChecks: err.body.failedChecks,
          details: err.body.details,
          overrideAllowed: !!err.body.overrideAllowed,
          userId: currentUser?.id,
          timeslotId: timeSlotData.timeslotId,
          bloodBankId,
          bookingStart,
        });
        finishOverwriteTimeslot();
        setIsDirectlyBooking(false);
        setForceConfirmModalOpen(true);
        return;
      }
      finishOverwriteTimeslot();
      throw err;
    }

    await updateDonorStatus();
    setIsAppointmentReschedule(false);
    finishOverwriteTimeslot();
  };

  const handleModalClose = () => {
    toggleBookAppointmentModal();
  };

  useEffect(() => {
    const initialChosenAppointmentValue = isDayPage ? currentAppointments.find(appointment => Number(appointment.value) === currentAppointment?.startDate) : null;
    
    setUserPnr("");
    setCurrentUser(null);
    setIsTiterMatch(false);
    setIsDonorTypeMatch(false);
    setIsInQuarantine(false);
    setIsDonorStatusPending(false);
    setIsConsultationAppointment(false);
    setSelectedAppointmentType(null);
    setEligibleAppointmentTypes([]);
    setEligibilityCheckLoading(false);
    setBookingEligibilityMissing(false);
    setEarliestDateWarning(null);
    setQuarantineWarning(null);
    if (!bookAppointmentModalIsOpen) {
      setChosenAppointment(initialChosenAppointmentValue);
    } else {
      if (currentAppointments.length === 1) {
        setChosenAppointment(currentAppointments[0]);
      }
    }
  }, [bookAppointmentModalIsOpen, currentAppointments, currentAppointment?.startDate, isDayPage]);

  const formatText = (text) => {
    const firstPart = text.slice(0, 6);
    const secondPart = text.slice(6, 11);
    return `${firstPart}\u00A0${secondPart}`;
  };

  const handleCopy = (e) => {
    e.clipboardData.setData("text/plain", userPnr);
    e.preventDefault();
  };

  const handleOverwrite = async () => {
    if (!selectedAppointmentType) {
      return;
    }
    toggleBookAppointmentModal();
    startOverwriteTimeslot();

    const appointmentValue = isSlotClickFromCell ? selectedTimeslot?.startDate : chosenAppointment.value;
    const bookingStart = Number(appointmentValue);
    const bookingEnd = bookingStart + selectedAppointmentType.duration;

    const needsCustomTimeslot =
      !isSlotClickFromCell &&
      (selectedAppointmentType.appointmentTypeId !== selectedTimeslot?.appointmentType?.appointmentTypeId ||
        selectedAppointmentType.duration !== selectedTimeslot?.appointmentType?.duration);

    const overlapResult = await handleOverlaps({
      bookingStart,
      bookingEnd,
      currentTimeslotId: selectedTimeslot?.timeslotId,
      applyAvailableExceptions: needsCustomTimeslot,
    });

    if (overlapResult?.blocked) {
      finishOverwriteTimeslot();
      return;
    };

    const timeSlotForOwerride = prepareTimeslotForUpdating(selectedTimeslot);

    let newAppointmentType;
    let newBloodTiter;
    let newDonorType;

    if (currentUser?.type === "full_blood") {
      if (currentUser?.status === "new") {
        newAppointmentType = appointmentTypes.find(
          (el) => el.donorStatus === "new",
        )?.appointmentTypeId;
        newBloodTiter = [...timeSlotForOwerride?.bloodTiter];
        newDonorType = [...timeSlotForOwerride?.donorType];
      } else if (currentUser?.status !== "new") {
        if (currentUser?.status === "established") {
          newAppointmentType = appointmentTypes.find(
            (el) =>
              el.donorStatus === "established" && el.name === `N'th time draw`,
          )?.appointmentTypeId;
        } else if (currentUser?.status === "approved") {
          newAppointmentType = appointmentTypes.find(
            (el) =>
              (el.donorStatus === "approved" && el.name === `First time draw`),
          )?.appointmentTypeId;
        } else if (currentUser?.status === "pending") {
          newAppointmentType = appointmentTypes.find(
            (el) => el.donorStatus === "pending" && el.name === "Consultation"
          )?.appointmentTypeId;
        }
        newBloodTiter = [...timeSlotForOwerride?.bloodTiter];
        newDonorType = [...timeSlotForOwerride?.donorType];
      }
    } else {
      newAppointmentType = appointmentTypes.find(
        (el) =>
          el.donorStatus === currentUser?.status &&
          el.donorType === currentUser?.type,
      )?.appointmentTypeId;
      newBloodTiter = [...timeSlotForOwerride?.bloodTiter];
      newDonorType = [...timeSlotForOwerride?.donorType];
    }

    const startOverwriteDate = +chosenAppointment.value;
    const targetAppType = appointmentTypes.find(
      (el) => el.appointmentTypeId === newAppointmentType
    );

    const endOverwriteDate = targetAppType?.duration
      ? startOverwriteDate + targetAppType.duration
      : NaN;

    let exceptionHoursOnDates = [...timeSlotForOwerride.exceptionHoursOnDates];

    if (!isNaN(startOverwriteDate) && !isNaN(endOverwriteDate)) {
      const exception = {
        from: timestampToIso(startOverwriteDate),
        to: timestampToIso(endOverwriteDate),
        date: timestampToIso(selectedTimeslot.partStart),
      };
      exceptionHoursOnDates.push(exception);
    }

    const updatedException = {
      ...timeSlotForOwerride,
      exceptionHoursOnDates: exceptionHoursOnDates,
      ...(recurrenceRule && { rule: recurrenceRule }),
    };

    const timeSlotForCreation = prepareTimeslotForCreation(
      {
        appointmentTypeId: newAppointmentType,
        donorType: newDonorType,
        bloodTiter: newBloodTiter,
        startDate: startOverwriteDate,
        endDate:
          startOverwriteDate +
          appointmentTypes.find(
            (el) => el.appointmentTypeId === newAppointmentType,
          )?.duration,
      },
      selectedTimeslot,
    );

    if (currentUser?.nextAppointment !== undefined) {
      await cancelAppointmentBooking(
        bloodBankId,
        currentUser.nextAppointment.reservationId,
        currentUser.id,
        currentUser.nextAppointment.startDate,
        bloodBankName,
      );
    }

    overwriteTimeslot(
      updatedException,
      timeSlotForCreation,
      bloodBankId,
      currentUser?.id,
      chosenAppointment.value,
    );
  };

  const isOverwriteDisabled =
    currentUser === null ||
    chosenAppointment === null

  const isBookDisabled = () => {
    if (eligibilityCheckLoading) {
      return true;
    }

    if (bookingEligibilityMissing) {
      return true;
    }

    if(isConsultationAppointment) {
      return false;
    }

    if (currentUser && eligibleAppointmentTypes.length > 0 && !selectedAppointmentType) {
      return true;
    }

    if (isDonorStatusPending) {
      return (
        currentUser === null ||
        (currentUser?.status !== selectedTimeslot?.appointmentType.donorStatus && !isSlotClickFromCell) ||
        (!isSlotClickFromCell && chosenAppointment === null)
      );
    }

    return (
      currentUser === null ||
      (currentUser?.status !== selectedTimeslot?.appointmentType.donorStatus && !isSlotClickFromCell) ||
      (!isSlotClickFromCell && chosenAppointment === null) ||
      (!isSlotClickFromCell && isTiterMatch) ||
      (!isSlotClickFromCell && isDonorTypeMatch)
    );
  };

  const isUserInQuarantine = () => {
    return !isConsultationAppointment && isInQuarantine;
  };

  const isUserStatusMatch = () => {
    return (
      !isSlotClickFromCell &&
      !isConsultationAppointment &&
      currentUser &&
      currentUser?.status !== selectedTimeslot?.appointmentType.donorStatus
    );
  };

  const isUserTiterMatch = () => {
    return !isSlotClickFromCell && !isConsultationAppointment && isTiterMatch && !isDonorStatusPending;
  };

  const isUserDonorTypeMatch = () => {
    return (
      !isSlotClickFromCell && !isConsultationAppointment && isDonorTypeMatch && !isDonorStatusPending
    );
  };

  return (
    <>
      <Modal
        isModalOpen={bookAppointmentModalIsOpen}
        handleModalClose={handleModalClose}
        {...props}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            {t("configuration.settings.bookAppointmentModal.header")}
          </h2>
          <button className="modal-close" onClick={handleModalClose}>
            <span className="icon-close" />
          </button>
        </div>

        <div className="modal-body modal-content">
          <div className="form-group d-flex items-center">
            <div className="flex-grow-0 modal-label">
              {t("configuration.settings.bookAppointmentModal.id")}
            </div>
            <div className="flex-grow-1">
              <Search
                type="text"
                placeholder={t(
                  "configuration.settings.bookAppointmentModal.searchPlaceholder"
                )}
                shortSearch={true}
                setValue={setUserPnr}
                autoComplete={"off"}
                isPnrSearch={true}
              />
            </div>
          </div>

          <div className={`form-group modal-text ${currentUser || "thin"}`}>
            {currentUser ? (
              `${currentUser.firstname} ${currentUser.lastname}`
            ) : userPnr === "" ? (
              ""
            ) : (
              <>
                <span>
                  {t("configuration.settings.bookAppointmentModal.noMatch")}
                </span>
                <span onCopy={handleCopy}>{` ${formatText(userPnr)}`}</span>
              </>
            )}
          </div>

          {currentAppointments.length > 1 && currentUser && (
            <div className="form-group">
              <CustomSelect
                label={t(
                  "configuration.settings.bookAppointmentModal.selectAppointmentTime"
                )}
                data={currentAppointments}
                value={chosenAppointment}
                onChange={setChosenAppointment}
                isSearchable={true}
              />
            </div>
          )}

          {/* Appointment type selector: use server-provided eligible types when donor is loaded */}
          {currentUser && eligibleAppointmentTypes.length > 0 && (
            <div className="form-group">
              <CustomSelect
                label={t("configuration.settings.bookAppointmentModal.appointmentType")}
                data={[
                  { label: t("configuration.settings.bookAppointmentModal.appointmentTypeSelect"), value: null, isPlaceholder: true },
                  ...eligibleAppointmentTypes.map((it) => ({ label: getAppointmentTypeDisplayName(it), value: it }))
                ]}
                value={selectedAppointmentType ? { label: getAppointmentTypeDisplayName(selectedAppointmentType), value: selectedAppointmentType } : null}
                onChange={(val) => setSelectedAppointmentType(val?.value || null)}
                isSearchable={true}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value?.appointmentTypeId || opt.value?.name || 'placeholder'}
              />
            </div>
          )}

          {isUserInQuarantine() ? (
            <div className="form-group modal-text thin d-flex justify-content-between">
              {t("configuration.settings.bookAppointmentModal.inQuarantine")}
            </div>
          ) : bookingEligibilityMissing ? (
            <div className="form-group modal-text thin" style={{ color: '#d0021b', fontWeight: 600 }}>
              {t("configuration.settings.bookAppointmentModal.bookingEligibilityMissing")}
            </div>
          ) : isUserStatusMatch() ? (
            <div className="form-group modal-text thin d-flex justify-content-between">
              {t("configuration.settings.bookAppointmentModal.statusNotMatch")}
              <Button
                disabled={isOverwriteDisabled}
                onClick={toggleOverwriteModal}
                className="btn-flat"
                text={` +${t("configuration.settings.add")}`}
              />
            </div>
          ) : earliestDateWarning ? (
            <div className="form-group modal-text thin d-flex justify-content-between">
              {t("configuration.settings.bookAppointmentModal.earliestDateWarning", {date: earliestDateWarning})}
            </div>
          ) : quarantineWarning ? (
            <div className="form-group modal-text thin d-flex justify-content-between">
              {t("configuration.settings.bookAppointmentModal.inQuarantine")}
            </div>
          ) : isUserTiterMatch() ? (
            <div className="form-group modal-text thin d-flex justify-content-between">
              {t("configuration.settings.bookAppointmentModal.titerNotMatch")}
              <Button
                disabled={isOverwriteDisabled}
                onClick={toggleOverwriteModal}
                className="btn-flat"
                text={` +${t("configuration.settings.add")}`}
              />
            </div>
          ) : isUserDonorTypeMatch() ? (
            <div className="form-group modal-text thin d-flex justify-content-between">
              {t(
                "configuration.settings.bookAppointmentModal.donorTypeNotMatch"
              )}
              <Button
                disabled={isOverwriteDisabled}
                onClick={toggleOverwriteModal}
                className="btn-flat"
                text={` +${t("configuration.settings.add")}`}
              />
            </div>
          ) : currentUser?.nextAppointment ? (
            <div className="form-group modal-text thin">
              {t("configuration.settings.bookAppointmentModal.nextAppointment")}
            </div>
          ) : (
            <></>
          )}
        </div>

        <div className="modal-footer">
          <div className="inputs-group">
            <div className="inputs-group-item">
              <Button
                type="button"
                text={t("common.cancel")}
                onClick={toggleBookAppointmentModal}
              />
            </div>
            <div className="inputs-group-item">
              <Button
                disabled={isBookDisabled()}
                btnClassName="btn-primary"
                type="button"
                text={t("common.book")}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isModalOpen={overlapModalOpen}
        handleModalClose={() => setOverlapModalOpen(false)}
        {...props}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            {t("configuration.settings.bookAppointmentModal.overlapTitle") ||
              "Cannot book appointment"}
          </h2>
          <button
            className="modal-close"
            onClick={() => setOverlapModalOpen(false)}
          >
            <span className="icon-close" />
          </button>
        </div>
        <div className="modal-body modal-content">
          <div className="modal-msg">
            {t("configuration.settings.bookAppointmentModal.overlapReserved")}
          </div>
        </div>
        <div className="modal-footer light w-100">
          <div className="inputs-group">
            <div className="inputs-group-item">
              <Button
                type="button"
                text={t("common.ok")}
                btnClassName="m-10"
                onClick={() => setOverlapModalOpen(false)}
              />
            </div>
          </div>
        </div>
      </Modal>
      <OverWriteModal
        isModalOpen={isOverwriteModalOpened}
        handleClose={toggleOverwriteModal}
        onApply={handleOverwrite}
        isInProgress={isOverwriting}
        isError={overwritingError}
      />
      <OverWriteModal
        isModalOpen={isAppointmentReschedule}
        handleClose={() => setIsAppointmentReschedule(false)}
        onApply={handleSubmitOverwrite}
        isConsultationAppointment={isConsultationAppointment}
        isDonorStatusPending={isDonorStatusPending}
      />
      <ForceConfirmModal
        isOpen={forceConfirmModalOpen}
        failedChecks={forceConfirmData?.failedChecks}
        details={forceConfirmData?.details}
        overrideAllowed={forceConfirmData?.overrideAllowed}
        userId={forceConfirmData?.userId}
        timeslotId={forceConfirmData?.timeslotId}
        bloodBankId={forceConfirmData?.bloodBankId}
        bookingStart={forceConfirmData?.bookingStart}
        onForceBookCustom={forceConfirmData?.onForceBookCustom}
        onClose={() => setForceConfirmModalOpen(false)}
        onSuccess={() => {
          setForceConfirmModalOpen(false);
          if (!forceConfirmData?.onForceBookCustom) {
            updateDonorStatus();
          }
        }}
        onError={(err) => {
          setForceConfirmModalOpen(false);
          finishOverwriteTimeslot();
        }}
      />
    </>
  );
};

export default BookAppointment;
